import Vue from 'vue';
import { MessageBox } from 'element-ui';
import store from '@/store';
import { queryByCampaignIds } from '@/api/adManagement';

// 非本人系列操作确认文案（不含关联人姓名）
export const NOT_OWNER_MSG = '当前操作对象关联人不是你，确认继续操作？';

// 页面级缓存：campaignId -> 关联关系(opt_info)
// 切tab不重查；切账户/手动刷新时调用 clearOptInfoCache 清空
const optInfoMap = new Map();
// 进行中的查询：campaignId -> Promise，避免并发重复请求
const inflightMap = new Map();

export function clearOptInfoCache() {
  optInfoMap.clear();
  inflightMap.clear();
}

function currentUserId() {
  return store.state.user.userInfo.data ? store.state.user.userInfo.data.id : null;
}

function getRowCampaignId(row) {
  if (!row) return '';
  if (row.level === 'campaign') {
    return String(row.campaign_id || row.id || '');
  }
  return row.campaign_id ? String(row.campaign_id) : '';
}

function getRowOptInfo(row) {
  const ref = row && row.campaign_ref;
  if (ref && typeof ref === 'object' && ref.opt_info) {
    return ref.opt_info;
  }
  return null;
}

function applyOptToRow(row, opt) {
  Vue.set(row, '_optInfo', opt || null);
  Vue.set(row, '_notOwner', isNotOwnerOpt(opt));
}

function parseQueryData(data) {
  const parsed = {};
  if (!data) return parsed;
  if (Array.isArray(data)) {
    data.forEach((item) => {
      const cid = item && (item.campaignId || item.campaign_id);
      if (!cid) return;
      parsed[String(cid)] = item.opt_info || item.optInfo || item;
    });
    return parsed;
  }
  if (typeof data === 'object') {
    Object.keys(data).forEach((cid) => {
      const raw = data[cid];
      parsed[String(cid)] = raw && (raw.opt_info || raw.optInfo) ? raw.opt_info || raw.optInfo : raw;
    });
  }
  return parsed;
}

function getOptName(opt) {
  if (!opt) return '';
  return opt.opt_name || opt.optimizerName || '';
}

// 关联关系存在且不是当前登录人 → 非本人；无关联 / 未登录 → false（可操作）
export function isNotOwnerOpt(opt) {
  const uid = currentUserId();
  if (!uid || !opt || !opt.opt_id) return false;
  return String(opt.opt_id) !== String(uid);
}

function resolveOpt(row) {
  if (!row) return null;
  if (row._optInfo) return row._optInfo;
  const fromRef = getRowOptInfo(row);
  if (fromRef) return fromRef;
  const cid = getRowCampaignId(row);
  return cid && optInfoMap.has(cid) ? optInfoMap.get(cid) : null;
}

function collectNotOwnerRows(rows) {
  const list = Array.isArray(rows) ? rows : [rows];
  return list.filter((v) => {
    if (!v) return false;
    if (v._notOwner) return true;
    return isNotOwnerOpt(resolveOpt(v));
  });
}

export function buildOwnerConfirmMessage(rows) {
  const names = [
    ...new Set(
      collectNotOwnerRows(rows)
        .map((v) => getOptName(resolveOpt(v)))
        .filter(Boolean),
    ),
  ];
  const nameText = names.length ? `（关联人：${names.join('、')}）` : '';
  return `当前操作对象关联人不是你${nameText}，确认继续操作？`;
}

// 操作前确认：本人 / 未关联直接放行；非本人弹确认框，确定后继续，取消返回 false
export async function guardOwner(rows) {
  const notOwnerRows = collectNotOwnerRows(rows);
  if (!notOwnerRows.length) return true;
  try {
    await MessageBox.confirm(buildOwnerConfirmMessage(notOwnerRows), '提示', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning',
    });
    return true;
  } catch (e) {
    return false;
  }
}

async function fetchMissingOptInfo(ids, platform) {
  const unique = [...new Set(ids.filter(Boolean))];
  const pending = [];
  const needFetch = [];

  unique.forEach((cid) => {
    if (optInfoMap.has(cid)) return;
    if (inflightMap.has(cid)) {
      pending.push(inflightMap.get(cid));
    } else {
      needFetch.push(cid);
    }
  });

  if (needFetch.length) {
    const req = queryByCampaignIds({
      platform,
      campaignIds: needFetch,
    })
      .then((res) => {
        if (!res || res.code != 0) return;
        const parsed = parseQueryData(res.data);
        needFetch.forEach((cid) => {
          optInfoMap.set(cid, parsed[cid] || null);
        });
      })
      .finally(() => {
        needFetch.forEach((cid) => inflightMap.delete(cid));
      });
    needFetch.forEach((cid) => inflightMap.set(cid, req));
    pending.push(req);
  }

  if (pending.length) {
    await Promise.all(pending);
  }
}

// 列表/子行数据返回后合并关联关系并标记 row._notOwner
// campaign 层级取列表自带的 campaign_ref.opt_info；adset/ad 层级按 campaign_id 查缓存，缺失部分调 giam 接口补齐
// fail-open：接口异常时不标记，默认放行
export async function enrichRowsOwnerInfo(rows, platform) {
  if (!Array.isArray(rows) || !rows.length) return;
  try {
    const missing = [];
    rows.forEach((row) => {
      if (!row || !row.level || row.level === 'asset') return;
      if (row.level === 'campaign') {
        const opt = getRowOptInfo(row);
        const cid = getRowCampaignId(row);
        if (cid) {
          optInfoMap.set(cid, opt);
        }
        applyOptToRow(row, opt);
        return;
      }
      const cid = getRowCampaignId(row);
      if (!cid) {
        applyOptToRow(row, null);
        return;
      }
      if (optInfoMap.has(cid)) {
        applyOptToRow(row, optInfoMap.get(cid));
      } else {
        missing.push(cid);
      }
    });
    if (missing.length) {
      await fetchMissingOptInfo(missing, platform);
      rows.forEach((row) => {
        if (!row || !row.level || row.level === 'campaign' || row.level === 'asset') return;
        const cid = getRowCampaignId(row);
        applyOptToRow(row, cid && optInfoMap.has(cid) ? optInfoMap.get(cid) : null);
      });
    }
  } catch (e) {
    console.warn('查询系列关联关系失败，默认放行操作', e);
  }
}
